import { useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { Dispatch, SetStateAction } from 'react';
import vconst from 'utils/helpers/structs/vconst';
import { isSameIdSet } from 'utils/helpers/list/isSameIdSet';
import SolarIcon from 'components/icons/SolarIcon';
import EToolTip from 'components/others/EToolTip';
import CatalogCategoryFilter from '../../CatalogCategory/CatalogCategoryFilter';

const defaultProductFilterStatus = [vconst.prodActive];

interface ProductFilterStatusProps {
    filterStatus: number[];
    setFilterStatus: Dispatch<SetStateAction<number[]>>;
    filterCategoryId: number | null;
    setFilterCategoryId: (id: number | null) => void;
}

const ProductFilterStatus = ({
    filterStatus,
    setFilterStatus,
    filterCategoryId,
    setFilterCategoryId,
}: ProductFilterStatusProps) => {
    const { t } = useTranslation(['Products', 'common']);
    const [show, setShow] = useState(false);
    const [draftStatus, setDraftStatus] = useState<number[]>([...filterStatus]);
    const [draftCategoryId, setDraftCategoryId] = useState<number | null>(filterCategoryId);
    const filterActive =
        !isSameIdSet(filterStatus, defaultProductFilterStatus) || Boolean(filterCategoryId);

    const handleHide = () => setShow(false);

    const handleOpen = () => {
        setDraftStatus([...filterStatus]);
        setDraftCategoryId(filterCategoryId);
        setShow(true);
    };

    const handleApply = () => {
        setFilterStatus([...draftStatus]);
        setFilterCategoryId(draftCategoryId);
        handleHide();
    };

    const handleReset = () => {
        setFilterStatus(defaultProductFilterStatus);
        setFilterCategoryId(null);
        handleHide();
    };

    return (
        <>
            <EToolTip title={t('toolFilter')}>
                <span className="d-inline-block">
                    <Button
                        className="fs-14 px-2 ms-1"
                        size="sm"
                        variant={filterActive ? 'danger' : 'primary'}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            handleOpen();
                        }}
                    >
                        <SolarIcon name="funnel" className="fs-14 p-0" />
                    </Button>
                </span>
            </EToolTip>

            <Offcanvas show={show} onHide={handleHide} placement="end">
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>{t('toolFilter')}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-filter-form d-flex flex-column h-100">
                        <div className="off-filter-content px-3">
                            <CatalogCategoryFilter
                                embedded
                                kind="product"
                                filterCategoryId={draftCategoryId}
                                setFilterCategoryId={setDraftCategoryId}
                            />

                            <div className="mb-3">
                                <label className="form-label text-muted fs-12 mb-1">{t('filterStatus')}</label>
                                <button
                                    type="button"
                                    className="off-filter-item"
                                    onClick={() => setDraftStatus([vconst.prodActive, vconst.prodArchive])}
                                >
                                    <input
                                        type="radio"
                                        checked={
                                            draftStatus?.includes(vconst.prodActive) &&
                                            draftStatus?.includes(vconst.prodArchive)
                                        }
                                        onChange={() => null}
                                        className="form-check-input me-2"
                                    />
                                    {t('prodArchive.all')}
                                </button>
                                <button
                                    type="button"
                                    className="off-filter-item"
                                    onClick={() => setDraftStatus([1])}
                                >
                                    <input
                                        type="radio"
                                        checked={
                                            draftStatus?.includes(vconst.prodActive) &&
                                            !draftStatus?.includes(vconst.prodArchive)
                                        }
                                        onChange={() => null}
                                        className="form-check-input me-2"
                                    />
                                    {t('prodArchive.active')}
                                </button>
                                <button
                                    type="button"
                                    className="off-filter-item text-danger"
                                    onClick={() => setDraftStatus([0])}
                                >
                                    <input
                                        type="radio"
                                        checked={
                                            !draftStatus?.includes(vconst.prodActive) &&
                                            draftStatus?.includes(vconst.prodArchive)
                                        }
                                        onChange={() => null}
                                        className="form-check-input me-2"
                                    />
                                    {t('prodArchive.archive')}
                                </button>
                            </div>
                        </div>
                        <div className="p-3 offcanvas-footer off-filter-footer">
                            <div className="d-flex justify-content-end gap-2">
                                <Button variant="secondary" onClick={handleReset}>
                                    {t('common:buttons.reset')}
                                </Button>
                                <Button variant="info" className="min100px" onClick={handleApply}>
                                    {t('common:buttons.apply')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default ProductFilterStatus;
